/**
 * usePushNotifications — Register this browser for Web Push (VAPID)
 *
 * Usage in go-pwa / web-client (inject api.post when calling):
 *
 *   const push = usePushNotifications({
 *     vapidPublicKeyUrl: '/api/v1/push/vapid-public-key',
 *     subscribeFn: (sub) => api.post('/push/subscribe', sub),
 *     unsubscribeFn: (ep) => api.delete('/push/unsubscribe', { endpoint: ep }),
 *     surface: 'go-pwa',
 *   });
 *
 * The hook wires up a listener for SW→window postMessages so the app
 * can react to push arrivals without a click (PUSH_NOTIFICATION event).
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  surface?: string;
  deviceLabel?: string;
}

export interface UsePushNotificationsOpts {
  /** URL to fetch the VAPID public key from (e.g. '/api/v1/push/vapid-public-key') */
  vapidPublicKeyUrl: string;
  /**
   * Function to register the subscription with the backend.
   * Called after SW subscribes successfully.
   */
  subscribeFn: (payload: PushSubscriptionPayload) => Promise<unknown>;
  /**
   * Function to remove the subscription from the backend.
   * Called when user opts out.
   */
  unsubscribeFn?: (endpoint: string) => Promise<unknown>;
  /** Surface identifier passed to server for device labeling */
  surface?: string;
  /** Called when a push notification message is received from the Service Worker */
  onPushMessage?: (payload: unknown) => void;
  /** Called when navigation is requested by the Service Worker */
  onNavigateTo?: (url: string) => void;
}

export type PushPermission = 'default' | 'granted' | 'denied';

export interface UsePushNotificationsReturn {
  /** Current browser notification permission state */
  permission: PushPermission;
  /** Whether we are currently registered with the backend */
  subscribed: boolean;
  /** True while requesting permission or registering */
  loading: boolean;
  /** Last error message, if any */
  error: string | null;
  /** Request permission + register subscription */
  subscribe: () => Promise<void>;
  /** Unregister subscription */
  unsubscribe: () => Promise<void>;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export function usePushNotifications(opts: UsePushNotificationsOpts): UsePushNotificationsReturn {
  const [permission, setPermission] = useState<PushPermission>(
    typeof Notification !== 'undefined' ? (Notification.permission as PushPermission) : 'default',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // Listen for SW messages (push broadcasts + navigation)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_NOTIFICATION') {
        optsRef.current.onPushMessage?.(event.data.payload);
      } else if (event.data?.type === 'NAVIGATE_TO' && event.data.url) {
        optsRef.current.onNavigateTo?.(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Check existing subscription on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => { if (sub) setSubscribed(true); })
      .catch(() => { /* ignore */ });
  }, []);

  const subscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setError('Web Push is not supported in this browser');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm as PushPermission);
      if (perm !== 'granted') {
        setError('Notification permission denied');
        return;
      }

      // 2. Fetch VAPID public key from backend
      const res = await fetch(optsRef.current.vapidPublicKeyUrl);
      const { data } = await res.json() as { data: { publicKey: string } };
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey) as unknown as ArrayBuffer;

      // 3. Subscribe via PushManager
      const reg = await navigator.serviceWorker.ready;
      const pushSub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      const json = pushSub.toJSON();
      if (!json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('Subscription missing encryption keys');
      }

      // 4. Register with backend
      await optsRef.current.subscribeFn({
        endpoint: pushSub.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        surface: optsRef.current.surface,
        deviceLabel: navigator.userAgent.slice(0, 100),
      });

      setSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe to push notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const reg = await navigator.serviceWorker.ready;
      const pushSub = await reg.pushManager.getSubscription();
      if (pushSub) {
        if (optsRef.current.unsubscribeFn) {
          await optsRef.current.unsubscribeFn(pushSub.endpoint);
        }
        await pushSub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe');
    } finally {
      setLoading(false);
    }
  }, []);

  return { permission, subscribed, loading, error, subscribe, unsubscribe };
}
